import { useMemo, useState } from "react";
import type { Analyte, StudyDetail } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { useToast } from "../lib/toast.js";
import { useDragReorder } from "../lib/useDragReorder.js";
import { errorMessage } from "../lib/useQuery.js";
import { Icon } from "./icons.js";
import { Button, IconButton } from "./ui.js";

/*
 * Catálogo → estudio → «Ordenar analitos» (Fernanda, 2026-11-02 20:56, on UROCULTIVO's
 * antibiogram: "solo mover los antibióticos"). The same interaction as Catálogo → Áreas: drag a
 * row by its handle (stylus, finger or mouse) or step it with the arrows (touch and keyboard),
 * then «Guardar orden» sends `catalogue.study.reorder_analytes` with every active analyte once.
 *
 * Subtitles move like any row; a row belongs to the nearest subtitle above it, so the indentation
 * shows live which section each analyte will print under. The server recomputes the sections the
 * same way. Orders already registered keep their frozen order (ADR 0009).
 */

const KIND_LABEL: Record<Analyte["kind"], string> = { numeric: "Numérico", text: "Texto", reference: "Opciones", subtitle: "Subtítulo", document: "Documento" };

export function activeInOrder(study: StudyDetail): Analyte[] {
  return study.analytes.filter((a) => a.is_active).sort((a, b) => a.sort_order - b.sort_order);
}

export function AnalyteOrderEditor({ study, onSaved, onCancel }: { study: StudyDetail; onSaved: (s: StudyDetail) => void; onCancel: () => void }) {
  const toast = useToast();
  const analytes = useMemo(() => activeInOrder(study), [study]);
  const initial = analytes.map((a) => a.id);
  const [order, setOrder] = useState<string[]>(initial);
  const [busy, setBusy] = useState(false);
  const [announce, setAnnounce] = useState("");
  const byId = useMemo(() => new Map(analytes.map((a) => [a.id, a])), [analytes]);
  const rows = order.map((id) => byId.get(id)).filter((a): a is Analyte => Boolean(a));
  const dirty = order.join(",") !== initial.join(",");
  const { dragId, rowRef, gripProps, step } = useDragReorder(setOrder);

  function stepAndAnnounce(a: Analyte, delta: number) {
    const to = order.indexOf(a.id) + delta;
    step(a.id, delta);
    setAnnounce(`${a.name}: posición ${to + 1} de ${order.length}.`);
  }

  async function save() {
    setBusy(true);
    try {
      const saved = await run<"catalogue.study.reorder_analytes", StudyDetail>("catalogue.study.reorder_analytes", { study_id: study.id, analyte_ids: order });
      toast.success(`Orden de los analitos de ${saved.clave} guardado (versión ${saved.version}).`);
      onSaved(saved);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  let inSection = false;
  return (
    <div className={`analyte-order${dragId ? " is-dragging" : ""}`}>
      <p className="small muted" style={{ margin: 0, padding: "12px 18px" }}>
        Arrastra cada renglón por la manija o usa las flechas. Los subtítulos se mueven igual; cada analito queda bajo el subtítulo que tenga arriba.
      </p>
      <div role="list" aria-label={`Analitos de ${study.clave} en orden de informe`}>
        {rows.map((a, i) => {
          const subtitle = a.kind === "subtitle";
          if (subtitle) inSection = true;
          const indented = !subtitle && inSection;
          const label = a.name || a.code;
          return (
            <div key={a.id} role="listitem" ref={rowRef(a.id)} className={`list-row${subtitle ? " subtitle" : ""}${indented ? " in-section" : ""}${dragId === a.id ? " dragging" : ""}`}>
              <span className="drag-handle" aria-hidden="true" title="Arrastrar" {...gripProps(a.id)}>
                <Icon name="grip" />
              </span>
              <span className="pos" aria-hidden="true">
                {i + 1}
              </span>
              <span className="name">
                <span className="primary">{label}</span>
                {!subtitle && (
                  <span className="secondary">
                    {KIND_LABEL[a.kind]}
                    {a.unit ? ` · ${a.unit}` : ""}
                  </span>
                )}
              </span>
              <span className="row" style={{ gap: 2, flex: "none" }}>
                <IconButton icon="arrowUp" label={`Subir ${label}`} disabled={i === 0 || busy} onClick={() => stepAndAnnounce(a, -1)} />
                <IconButton icon="arrowDown" label={`Bajar ${label}`} disabled={i === rows.length - 1 || busy} onClick={() => stepAndAnnounce(a, 1)} />
              </span>
            </div>
          );
        })}
      </div>
      <p className="small muted" style={{ margin: 0, padding: "12px 18px 4px" }}>
        El orden nuevo se aplica a las solicitudes que se registren después de guardarlo. Las ya registradas conservan su informe como estaba.
      </p>
      <span className="sr-only" role="status" aria-live="polite">
        {announce}
      </span>
      <div className="analyte-order-bar">
        <span className={`status small ${dirty ? "" : "muted"}`} style={dirty ? { color: "var(--amber)", fontWeight: 600 } : undefined}>
          {dirty ? "Cambios sin guardar" : "Sin cambios"}
        </span>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          {dirty ? "Descartar" : "Cerrar"}
        </Button>
        <Button variant="primary" icon="check" busy={busy} disabled={!dirty} onClick={() => void save()}>
          Guardar orden
        </Button>
      </div>
    </div>
  );
}
